import {
  BaseBlok,
  createRocheButtonTrackingObject,
  DateFormattingService,
  handleRocheButtonOnClick,
  MediaItem,
  RocheXXTranslationContext,
  SearchTrackingData,
  StringService,
  useComponentTracking,
  useTranslation,
} from '@repo/roche-xx-shared';
import { RocheUIButton, RocheUIButtonAppearance, RocheUITeaser, RocheUITeaserLayout } from '@rocheglobal/roche-ui';
import React, { useContext, useEffect, useState } from 'react';
import { RocheModal } from '../../../components/custom/roche-modal';
import { getComponent } from '../../../helpers/get-component';
import { trackSearchItemClickEvent } from '../../tracking';
import { EventItem } from '../types';

export interface RocheEventListItemProps {
  item: Partial<MediaItem> | Partial<EventItem>;
  trackingData: SearchTrackingData;
}

const isEventItem = (item: Partial<MediaItem> | Partial<EventItem>): item is Partial<EventItem> => {
  return 'Event Title' in item || 'Start Date' in item || 'Add to Calendar' in item;
};

const isMediaItem = (item: Partial<MediaItem> | Partial<EventItem>): item is Partial<MediaItem> => {
  return 'Title' in item || 'ReleaseDateTime' in item || 'Slug' in item;
};

export const RocheEventListItem = ({ item, trackingData }: RocheEventListItemProps) => {
  const [showModal, setShowModal] = useState(false);
  const translations = useContext(RocheXXTranslationContext);
  const buttonLabel = useTranslation('add to calendar');
  const readMoreLabel = useTranslation('read more');
  const teaserOverline = (item) => {
    const startDate = DateFormattingService.formatDate(
      item['Start Date'] as string | Date,
      'dateEvents',
      translations.translations,
    );
    return `
      ${startDate} ${item.Timezone}${item.Location !== null && item.Location !== '' ? ` | ${item.Location}` : ''}
    `;
  };

  const isMedia = isMediaItem(item);
  const isEvent = isEventItem(item);

  const teaserUrl = isMedia ? item.link : isEvent ? item.Link : undefined;

  const { path } = useComponentTracking();

  const handleCalendarClick = (clickEvent: React.MouseEvent) => {
    const text = StringService.capitalize(buttonLabel);
    handleRocheButtonOnClick(clickEvent, createRocheButtonTrackingObject({ text, componentPath: path }));
    setShowModal(true);
  };

  useEffect(() => {
    const handleModalClose = () => {
      setShowModal(false);
    };

    window.addEventListener('rocheModalClose', handleModalClose);

    return () => {
      window.removeEventListener('rocheModalClose', handleModalClose);
    };
  }, []);

  return (
    <>
      <RocheUITeaser
        slot='left'
        layout={RocheUITeaserLayout.CARD_C}
        teaserTitle={StringService.capitalize(item['Event Title'])}
        teaserOverline={teaserOverline(item)}
        teaserUrl={teaserUrl}
        forceAlignLeft={true}
        primaryButtonText={readMoreLabel}
        secondaryButton={
          item['Add to Calendar'] === 'TRUE' && (
            <div slot='secondary-action'>
              <RocheUIButton
                appearance={RocheUIButtonAppearance.TERTIARY}
                leftIcon='calendar'
                text={StringService.capitalize(buttonLabel)}
                onClick={handleCalendarClick}
              />
            </div>
          )
        }
        onTeaserClick={(e) => trackSearchItemClickEvent(e, trackingData, item['Event Title'], teaserUrl, path)}
      />
      {showModal && item['Add to Calendar'] === 'TRUE' && (
        <RocheModal
          dialogLabel={StringService.capitalize(item['Event Title'])}
          blok={{
            slotted: [
              {
                component: 'roche-add-to-calendar',
                eventTitle: StringService.capitalize(item['Event Title']),
                description: StringService.capitalize(item['Event Text'] || ''),
                startDate: item['Start Date'].replace(/\s/, 'T'),
                endDate: item['End Date'].replace(/\s/, 'T'),
              } as BaseBlok,
            ],
          }}
          getComponent={getComponent}
        />
      )}
    </>
  );
};
